import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import ClickOutside from 'Component/ClickOutside';
import CloseIcon from 'Component/CloseIcon';
import Loader from 'Component/Loader';
import SearchIcon from 'Component/SearchIcon';
import SearchOverlay from 'Component/SearchOverlay';
import { RefType } from 'Type/Common.type';
import { DeviceType } from 'Type/Device.type';

import 'Component/SearchField/SearchField.style';
import './SearchField.override.style';

/**
 * Override: complete override of SearchField component to move all functions to container
 * @namespace PeggysagePwa/Component/SearchField/Component */
export class SearchFieldComponent extends PureComponent {
    static propTypes = {
        searchCriteria: PropTypes.string.isRequired,
        onSearchBarFocus: PropTypes.func.isRequired,
        isVisible: PropTypes.bool.isRequired,
        isActive: PropTypes.bool.isRequired,
        device: DeviceType.isRequired,
        isHideOverlay: PropTypes.bool.isRequired,
        isClickOutsideDisabled: PropTypes.bool.isRequired,
        searchBarRef: RefType.isRequired,
        onClearSearchButtonClick: PropTypes.func.isRequired,
        onSearchEnterPress: PropTypes.func.isRequired,
        openSearch: PropTypes.func.isRequired,
        closeSearch: PropTypes.func.isRequired,
        handleChange: PropTypes.func.isRequired,
        clearSearch: PropTypes.func.isRequired
    };

    /**
     * Override: call container functions
     * @returns {JSX.Element}
     */
    renderClearSearch() {
        const { isVisible, searchCriteria, onClearSearchButtonClick } = this.props;

        return (
            <button
              block="SearchField"
              elem="ClearButton"
              onClick={ onClearSearchButtonClick }
              mods={ {
                  isVisible: isVisible && !!searchCriteria
              } }
              aria-label="Clear search"
            >
                { __('Clear') }
            </button>
        );
    }

    renderOverlayFallback() {
        return <Loader isLoading />;
    }

    /**
     * Override: add isHideOverlay variable for popup
     * @returns {JSX.Element}
     */
    renderSearch() {
        const {
            searchCriteria,
            onSearchBarFocus,
            isActive,
            device,
            isHideOverlay,
            searchBarRef,
            onSearchEnterPress,
            handleChange,
            clearSearch
        } = this.props;

        return (
            <div
              block="SearchField"
              elem="SearchInnerWrapper"
            >
                <div block="SearchField" elem="FieldWrapper">
                    <input
                      // id="search-field"
                      ref={ searchBarRef }
                      block="SearchField"
                      elem="Input"
                      onFocus={ onSearchBarFocus }
                      onChange={ handleChange }
                      onKeyDown={ onSearchEnterPress }
                      value={ searchCriteria }
                      mods={ { isActive } }
                      placeholder={ __('Search products') }
                      autoComplete="off"
                      aria-label={ __('Search') }
                    />
                    { this.renderSearchIcon() }
                    { this.renderClearSearch() }
                    { this.renderCloseIcon() }
                </div>
                <SearchOverlay
                  isHideOverlay={ isHideOverlay ?? !device.isMobile }
                  clearSearch={ clearSearch }
                  searchCriteria={ searchCriteria }
                />
            </div>
        );
    }

    /**
     * Override: call container functions
     * @returns {JSX.Element}
     */
    renderSearchIcon() {
        const { openSearch } = this.props;

        return (
            <div
              block="SearchField"
              elem="SearchIcon"
              role="button"
              tabIndex="0"
              onClick={ openSearch }
              onKeyDown={ openSearch }
              aria-label={ __('Search') }
            >
                <SearchIcon />
            </div>
        );
    }

    renderCloseIcon() {
        const { isActive, closeSearch, device: { isMobile } } = this.props;

        if (!isActive && isMobile) {
            return null;
        }

        return (
            <div
              block="SearchField"
              elem="CloseIcon"
              role="button"
              tabIndex="0"
              onClick={ closeSearch }
              onKeyDown={ closeSearch }
              aria-label={ __('Close') }
            >
                <CloseIcon />
            </div>
        );
    }

    /**
     * Override: add option to disable ClickOutside
     * @returns {JSX.Element}
     */
    render() {
        const {
            isVisible,
            isActive,
            isClickOutsideDisabled,
            closeSearch
        } = this.props;

        if (isClickOutsideDisabled) {
            return (
                <div block="SearchField" mods={ { isVisible, isActive } }>
                    <div block="SearchField" elem="Wrapper">
                        { this.renderSearch() }
                    </div>
                </div>
            );
        }

        return (
            <div block="SearchField" mods={ { isVisible, isActive } }>
                <ClickOutside onClick={ closeSearch }>
                    <div block="SearchField" elem="Wrapper">
                        { this.renderSearch() }
                    </div>
                </ClickOutside>
            </div>
        );
    }
}

export default SearchFieldComponent;
